/**
 * Put the page back where it was once the software keyboard has left.
 *
 * iOS Brave, landscape, typing a room code into the lobby's Join field: the
 * keyboard rose, the code went in, the keyboard went away — and a black band
 * stayed under the lobby, about a twentieth of the page box, until a reload.
 * The earlier black band under a zoomed page was closed by never letting the
 * page zoom (zoom-policy.js); a keyboard cannot be refused.
 *
 * What the engine does. When a text field takes focus, WebKit scrolls the
 * DOCUMENT so the field sits above the keys, whether or not the root is
 * `overflow: hidden` — and on a phone mobile.css locks it, because every
 * surface owns an inner scroll lane and the document itself never scrolls.
 * The offset moves the layout viewport, so the `100dvh` app shell moves up
 * with it. When the keyboard goes, the visual viewport grows back but the
 * offset is left where it was: the shell now ends `scrollY` pixels above the
 * bottom edge, and what shows below it is the html canvas, which is black. A
 * page that was allowed to scroll would show its own content there; a locked
 * one shows nothing, and that nothing is the band.
 *
 * So once the keyboard is gone the document is put back to 0,0. Two signals
 * say it is gone and both are used, because neither is reliable alone: the
 * field losing focus (a tap on Join, on Cancel, on the wood), and the visual
 * viewport growing back to the layout viewport (Android Chrome, and iOS when
 * the keyboard's own Done key hides it while the field keeps focus). The
 * reset waits out the keyboard's dismissal animation and runs only while the
 * root is locked and no keyboard is up, so a page that legitimately scrolls
 * is never touched and a field that still has the keys under it is never
 * yanked out of view. Phones and tablets only: a desktop has no software
 * keyboard, and its root is not locked either.
 */
import { Platform } from './platform.js';

let KeyboardSettle = {};

KeyboardSettle = (function () {
    /* The same threshold voucher-redeem.js uses to tell a keyboard from a
       browser bar: a URL bar is under 100px on every profile in
       tests/e2e-playwright/helpers/mobileViewports.js; a keyboard takes
       130-190px of a 300px page box. */
    const KEYBOARD_MIN_SHRINK_PX = 100;
    /* iOS animates the keyboard away over roughly a quarter of a second and
       reports the visual viewport only once it has settled; the later ticks
       catch a document that was still on the move at the first. */
    const SETTLE_TICKS_MS = [0, 120, 400, 800];
    const TEXT_INPUT_TYPES = new Set([
        'text', 'search', 'url', 'tel', 'email', 'password', 'number', 'date',
        'datetime-local', 'month', 'week', 'time',
    ]);

    let installed = false;
    let timers = [];

    function isTextControl(element) {
        if (!element || element === document.body) return false;
        const tag = element.tagName;
        if (tag === 'TEXTAREA') return true;
        if (tag === 'INPUT') return TEXT_INPUT_TYPES.has(String(element.type || 'text').toLowerCase());
        return element.isContentEditable === true;
    }

    function rootIsLocked() {
        const root = document.documentElement;
        if (!root || typeof window.getComputedStyle !== 'function') return false;
        const style = window.getComputedStyle(root);
        return style.overflowY === 'hidden' || style.overflow === 'hidden';
    }

    function keyboardUp() {
        const viewport = window.visualViewport;
        if (!viewport) {
            /* No visual viewport to read: a focused text field is the only
               evidence of a keyboard, so treat it as one. */
            return isTextControl(document.activeElement);
        }
        return viewport.height < window.innerHeight - KEYBOARD_MIN_SHRINK_PX;
    }

    /**
     * Undo the document offset a keyboard left behind.
     * @returns {boolean} whether anything was moved
     */
    function settle() {
        if (!rootIsLocked() || keyboardUp()) return false;
        const scroller = document.scrollingElement || document.documentElement;
        const offsetY = Math.max(
            window.scrollY || window.pageYOffset || 0,
            scroller ? scroller.scrollTop || 0 : 0,
            document.body ? document.body.scrollTop || 0 : 0,
        );
        const offsetX = Math.max(
            window.scrollX || window.pageXOffset || 0,
            scroller ? scroller.scrollLeft || 0 : 0,
        );
        if (offsetY <= 0 && offsetX <= 0) return false;
        try { window.scrollTo(0, 0); } catch (_) { /* an engine without it falls through to the elements */ }
        if (scroller) {
            scroller.scrollTop = 0;
            scroller.scrollLeft = 0;
        }
        if (document.body) document.body.scrollTop = 0;
        return true;
    }

    function clearTimers() {
        timers.forEach((timer) => clearTimeout(timer));
        timers = [];
    }

    function settleSoon() {
        clearTimers();
        timers = SETTLE_TICKS_MS.map((delay) => setTimeout(settle, delay));
    }

    function onFocusOut(event) {
        if (!isTextControl(event.target)) return;
        /* Moving to another field keeps the keyboard up; settle() will see
           that and do nothing, so the ticks are harmless — but skip the work. */
        if (isTextControl(event.relatedTarget)) return;
        settleSoon();
    }

    function onViewportResize() {
        if (keyboardUp()) return;
        settleSoon();
    }

    function install() {
        if (installed) return false;
        installed = true;
        const touchFirst = Boolean(Platform?.isMobileUA?.() || Platform?.isTouchPrimary?.());
        if (!touchFirst) return false;
        document.addEventListener('focusout', onFocusOut, true);
        window.visualViewport?.addEventListener?.('resize', onViewportResize);
        return true;
    }

    function destroy() {
        if (!installed) return;
        installed = false;
        clearTimers();
        document.removeEventListener('focusout', onFocusOut, true);
        window.visualViewport?.removeEventListener?.('resize', onViewportResize);
    }

    if (typeof document !== 'undefined') {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => { install(); }, { once: true });
        } else {
            install();
        }
    }

    return { install, settle, destroy };
})();

export { KeyboardSettle };
